import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import BeforeAfter from "../components/homi/BeforeAfter";
import GalleryGrid from "../components/homi/GalleryGrid";
import ContactCta from "../components/homi/ContactCta";
import { useContent } from "../context/ContentContext";
import { getProjectDetail } from "../data/details";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const { content } = useContent();
  const projects = content.projects || [];
  const idx = projects.findIndex((p) => String(p.id) === String(id));
  const project = idx >= 0 ? projects[idx] : projects[0];
  const detail = getProjectDetail(project?.id);
  const others = projects.filter((p) => p.id !== project?.id).slice(0, 3);
  const prev = idx > 0 ? projects[idx - 1] : null;
  const next = idx >= 0 && idx < projects.length - 1 ? projects[idx + 1] : null;
  const gallery = [...new Set([project?.image, project?.before, ...others.map((o) => o.image)].filter(Boolean))];

  return (
    <div className="App bg-white">
      <SiteHeader />
      <main>
        <PageBanner title={project?.title || "Project"} crumb={project?.title || "Project"} subtitle={project?.category} />

        <section className="py-20 lg:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            {project?.before ? (
              <BeforeAfter before={project.before} after={project.image} />
            ) : (
              <div className="rounded-3xl overflow-hidden shadow-2xl"><img src={project?.image} alt={project?.title} className="w-full h-[480px] object-cover" /></div>
            )}

            <div className="grid lg:grid-cols-3 gap-12 mt-14">
              <div className="lg:col-span-2">
                <span className="homi-section-tag text-homi-goldDark text-sm">Project Overview</span>
                <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-homi-black mt-3 mb-5">{project?.title}</h2>
                <p className="text-homi-body leading-relaxed text-lg mb-8">{detail.description}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {["Delivered on time & budget", "Premium, warrantied materials", "Licensed & insured crew", "Full clean-up on completion"].map((b) => (
                    <div key={b} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-homi-gold/15 flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-homi-goldDark" /></span>
                      <span className="font-medium text-homi-black">{b}</span>
                    </div>
                  ))}
                </div>
              </div>

              <aside>
                <div className="bg-homi-light rounded-2xl p-6">
                  <h4 className="font-heading font-bold text-lg text-homi-black mb-4">Project Details</h4>
                  <ul className="divide-y divide-gray-200">
                    {Object.entries(detail.specs).map(([k, v]) => (
                      <li key={k} className="flex items-center justify-between py-3">
                        <span className="text-homi-body">{k}</span>
                        <span className="font-heading font-semibold text-homi-black">{v}</span>
                      </li>
                    ))}
                    <li className="flex items-center justify-between py-3">
                      <span className="text-homi-body">Category</span>
                      <span className="font-heading font-semibold text-homi-black">{project?.category}</span>
                    </li>
                  </ul>
                  <Link to="/homi/contact" className="homi-btn-shine mt-5 block text-center bg-homi-gold hover:bg-homi-goldDark text-homi-black font-heading font-bold px-6 py-3 rounded-lg transition-colors">Start Your Project</Link>
                </div>
              </aside>
            </div>

            {/* Gallery */}
            <div className="mt-16">
              <h3 className="font-heading font-bold text-2xl text-homi-black mb-6">Project Gallery</h3>
              <GalleryGrid images={gallery} cols={3} />
            </div>

            {/* Prev / Next */}
            <div className="flex items-center justify-between mt-14 pt-8 border-t border-gray-100">
              {prev ? (
                <Link to={`/homi/projects/${prev.id}`} className="inline-flex items-center gap-2 font-heading font-bold text-homi-black hover:text-homi-goldDark transition-colors"><ArrowLeft className="w-5 h-5" /> {prev.title}</Link>
              ) : <span />}
              {next ? (
                <Link to={`/homi/projects/${next.id}`} className="inline-flex items-center gap-2 font-heading font-bold text-homi-black hover:text-homi-goldDark transition-colors">{next.title} <ArrowRight className="w-5 h-5" /></Link>
              ) : <span />}
            </div>

            {/* Other projects */}
            <h3 className="font-heading font-bold text-2xl text-homi-black mt-16 mb-6">More Projects</h3>
            <div className="grid sm:grid-cols-3 gap-6">
              {others.map((p) => (
                <Link to={`/homi/projects/${p.id}`} key={p.id} className="group rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all">
                  <div className="aspect-[4/3] overflow-hidden"><img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /></div>
                  <div className="p-4">
                    <span className="text-homi-goldDark text-xs font-heading font-bold uppercase">{p.category}</span>
                    <h4 className="font-heading font-bold text-homi-black mt-1">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <ContactCta />
      </main>
      <Footer />
    </div>
  );
}
